import { useState, useCallback, useRef } from 'react';
import Cropper from 'react-easy-crop';
import { toast } from 'sonner';
import { Upload, Trash2, Camera, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import api from '@/lib/api';

const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];

/**
 * Crop an HTMLImageElement region to a square Blob (JPEG, 400x400).
 */
async function getCroppedJpegBlob(imageSrc, area) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = imageSrc;
  });
  const canvas = document.createElement('canvas');
  const SIZE = 400;
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    img,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    SIZE,
    SIZE,
  );
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.9));
}

export default function ProfilePhotoUploader({
  currentPhotoUrl,
  firstName = '',
  lastName = '',
  size = 128,
  onUploaded,
  onDeleted,
  testIdPrefix = 'profile-photo',
}) {
  const [cropOpen, setCropOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const inputRef = useRef(null);

  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
  const photoSrc = currentPhotoUrl
    ? (currentPhotoUrl.startsWith('http') ? currentPhotoUrl : `${process.env.REACT_APP_BACKEND_URL}${currentPhotoUrl}`)
    : null;

  const handleFilePick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting same file
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WEBP image');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('Photo must be under 5MB — please try a smaller image');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setImageSrc(ev.target.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirmCrop = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setUploading(true);
    try {
      const blob = await getCroppedJpegBlob(imageSrc, croppedAreaPixels);
      const fd = new FormData();
      fd.append('file', blob, 'profile_photo.jpg');
      const res = await api.post('/upload/profile-photo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Profile photo updated');
      setCropOpen(false);
      setImageSrc(null);
      onUploaded?.(res.data.url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed — please try again');
    }
    setUploading(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to remove your profile photo?')) return;
    setDeleting(true);
    try {
      await api.delete('/upload/profile-photo');
      toast.success('Profile photo removed');
      onDeleted?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove photo — please try again');
    }
    setDeleting(false);
  };

  return (
    <div className="flex flex-col items-center gap-3" data-testid={`${testIdPrefix}-uploader`}>
      <div className="relative" style={{ width: size, height: size }}>
        <Avatar className="w-full h-full">
          {photoSrc ? <AvatarImage src={photoSrc} alt="Profile" data-testid={`${testIdPrefix}-img`} /> : null}
          <AvatarFallback className="bg-seafoam/40 text-teal text-3xl font-semibold" data-testid={`${testIdPrefix}-initials`}>
            {initials}
          </AvatarFallback>
        </Avatar>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="absolute bottom-0 right-0 bg-teal text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-teal-deep transition-colors shadow-lg"
          data-testid={`${testIdPrefix}-camera-btn`}
          aria-label="Add or change profile photo"
        >
          <Camera size={18} />
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          onChange={handleFilePick}
          className="hidden"
          data-testid={`${testIdPrefix}-file-input`}
        />
      </div>

      {!photoSrc && (
        <p className="text-xs text-text-mid">Add your photo</p>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => inputRef.current?.click()}
          className="rounded-full text-xs"
          data-testid={`${testIdPrefix}-change-btn`}
        >
          <Upload size={14} className="mr-1" /> {photoSrc ? 'Change Photo' : 'Upload Photo'}
        </Button>
        {photoSrc && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={handleDelete}
            disabled={deleting}
            className="rounded-full text-xs text-red-600 border-red-300 hover:bg-red-50"
            data-testid={`${testIdPrefix}-delete-btn`}
          >
            {deleting ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Trash2 size={14} className="mr-1" />}
            Delete Photo
          </Button>
        )}
      </div>

      {/* Crop Modal */}
      <Dialog open={cropOpen} onOpenChange={(open) => { if (!uploading) setCropOpen(open); }}>
        <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-crop-modal`}>
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Adjust Your Photo</DialogTitle>
            <DialogDescription>Drag to reposition. Pinch or use the slider to zoom.</DialogDescription>
          </DialogHeader>
          <div className="relative w-full" style={{ height: 320 }}>
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full accent-teal"
            data-testid={`${testIdPrefix}-zoom-slider`}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setCropOpen(false); setImageSrc(null); }}
              disabled={uploading}
              className="rounded-full"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmCrop}
              disabled={uploading || !croppedAreaPixels}
              className="bg-teal hover:bg-teal-deep text-white rounded-full"
              data-testid={`${testIdPrefix}-confirm-crop-btn`}
            >
              {uploading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Save Photo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
